import React, { useMemo, useState } from "react";
import { PageProps, graphql } from "gatsby";
import HeroBanner from "@components/common/HeroBanner";
import { AuthorResponseModel } from "@models/users";
import TagFilter from "@components/common/TagFilter";
import useTagFilter from "@hooks/use-tag-filter";
import {
  AllBlogRequestOneBlogPreviewDataModel,
  BlogListingPageDataMode,
} from "@models/blog";
import ImageRenderer from "@components/common/Image";
import { getFirstIfArray, isEmpty } from "@utils/common";
import Pill from "@components/common/Pill";
import Link from "@components/common/Link";
import Input from "@components/common/Input";
import { useFlexSearch } from "react-use-flexsearch";
import { AiOutlineSearch } from "react-icons/ai";
import Layout from "@components/common/Layout";
import Pagination from "@components/common/Pagination";

const BlogListingPage = (props: PageProps<BlogListingPageDataMode>) => {
  const [inputText, setInputText] = useState("");
  const [query, setQuery] = useState<string | null>(null);
  const {
    strapiBlogHome,
    allStrapiBlogTag,
    allStrapiBlog,
    localSearchBlogsIndex,
  } = props?.data ?? {};
  const {
    // author,
    title,
    bigHeader,
    ctaLink,
    ctaText,
    topbanner,
  } = strapiBlogHome;
  const allTags = allStrapiBlogTag?.edges;
  const allBlogs = allStrapiBlog?.edges ?? [];
  const {
    selectedFilters,
    isAllSelected,
    handleAllSelection,
    handleFilterClick,
    resetTagFilter,
  } = useTagFilter();
  const indexSearchResult = useFlexSearch(
    query,
    localSearchBlogsIndex.index,
    localSearchBlogsIndex.store
  ) as { slug: string }[];
  const flatIndexedSlug =
    indexSearchResult?.map((idxSearch) => idxSearch?.slug) ?? [];
  const indexSearchedBlog = allBlogs?.filter((blog) => {
    const { slug } = blog?.node ?? {};
    return flatIndexedSlug.includes(slug);
  });
  const tagFilteredBlogs = useMemo(() => {
    const filteredBlogs: AllBlogRequestOneBlogPreviewDataModel[] = [];
    allBlogs.forEach((oneWeb) => {
      const { blog_tags: blogTags } = oneWeb?.node ?? {};
      const flatTagSlug = blogTags?.map((t) => t.slug);
      if (selectedFilters.some((f) => flatTagSlug.includes(f))) {
        filteredBlogs.push(oneWeb);
      }
    });
    return filteredBlogs;
  }, [selectedFilters]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const str = e.target.value;
    setInputText(str);
    if (!str?.length) {
      setQuery(null);
    }
  };

  const handleSearchCick = () => {
    if (inputText?.length) {
      resetTagFilter();
      setQuery(inputText);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputText?.length) {
      handleSearchCick();
    }
  };

  const renderAuthor = (author: AuthorResponseModel | null) => {
    if (!author || isEmpty(author)) return <></>;
    const {
      firstName = "",
      lastName = "",
      profilePicture,
      subTitle = "",
    } = author;
    return (
      <div className="flex items-center">
        <div className="w-16 h-16 rounded-full mr-3 overflow-hidden">
          <ImageRenderer
            image={profilePicture}
            alt={`${firstName} ${lastName}`}
            className="rouned-full"
          />
        </div>
        <div className="flex flex-col justify-center">
          <div className="text-lg font-semibold">
            {firstName + " " + lastName}
          </div>
          <div className=" text-base font-normal">{subTitle}</div>
        </div>
      </div>
    );
  };

  const renderOneBlog = (blog: AllBlogRequestOneBlogPreviewDataModel) => {
    const {
      title,
      topBanner,
      author,
      blog_tags: blogTags,
      slug,
    } = blog?.node ?? {};
    const firstAuthor = getFirstIfArray(author);
    const blogUrl = `/blogs/${slug}`;
    return (
      <div className="w-[343px] sm:w-[200px] lg:w-[270px] p-[18px] shadow-lg rounded-2xl h-full relative bg-white flex flex-col">
        <div className="rounded-lg overflow-hidden mb-4">
          {topBanner?.image ? (
            <ImageRenderer image={topBanner?.image} alt={title} />
          ) : (
            <></>
          )}
        </div>
        <div className="flex flex-wrap">
          {blogTags.map((t) => (
            <span key={t?.id} className="my-2">
              <Pill type="primary">{t?.title}</Pill>
            </span>
          ))}
        </div>
        <div className="grow flex flex-col justify-between">
          <Link
            href={blogUrl}
            linkText={title}
            wrapperClassName="block max-w-[80%] text-xl my-8"
            className="no-underline hover:font-normal"
            style={{
              color: "inherit",
              fontSize: "inherit",
              wordBreak: "break-word",
            }}
          />
          {renderAuthor(firstAuthor)}
        </div>
      </div>
    );
  };

  const renderBlogs = () => {
    if (selectedFilters?.length) {
      return tagFilteredBlogs?.map((oneBlogPreview) => {
        return (
          <div key={oneBlogPreview?.node?.id} className="flex justify-center">
            {renderOneBlog(oneBlogPreview)}
          </div>
        );
      });
    } else if (query?.length) {
      return indexSearchedBlog.map((oneBlogPreview) => {
        return (
          <div key={oneBlogPreview?.node?.id} className="flex justify-center">
            {renderOneBlog(oneBlogPreview)}
          </div>
        );
      });
    } else {
      return allBlogs?.map((oneBlogPreview) => {
        return (
          <div key={oneBlogPreview?.node?.id} className="flex justify-center">
            {renderOneBlog(oneBlogPreview)}
          </div>
        );
      });
    }
  };
  return (
    <Layout>
      <div>
        <HeroBanner
          title={title}
          bigHeader={bigHeader}
          ctaBtnLink={ctaLink}
          ctaBtnText={ctaText}
          path=""
          description=""
          appendToPath=""
          topbanner={topbanner}
          // author={author}
          bigHeaderClassName="text-2xl md:text-2xl font-semibold leading-[32px] xl:text-4xl xl:leading-[48px] 2xl:text-4xl 2xl:leading-[48px]"
        />
        <div className="bg-gray-100 md:py-20">
          <div className="md:pl-[140px] md:pr-8 md:flex md:gap-[89px]">
            <div className="shrink-0">
              <TagFilter
                tags={allTags}
                isAllSelected={isAllSelected}
                selectedFilters={selectedFilters}
                handleAllSelection={handleAllSelection}
                handleFilterClick={handleFilterClick}
              />
            </div>
            <div className="mb-[67px] grow-0">
              <div className="max-w-[60%] md:max-w-[530px] relative px-4 mx-auto mb-4 md:mx-0 md:px-0">
                <Input
                  placeholder="Search Blogs"
                  value={inputText}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                />
                <AiOutlineSearch
                  className="absolute fill-gray-400 right-7 top-5 cursor-pointer"
                  size={24}
                  onClick={handleSearchCick}
                />
              </div>
              <Pagination
                itemsPerPage={12}
                childWrapperClassName="flex flex-col items-stretch md:flex-row flex-wrap gap-6"
              >
                {renderBlogs()}
              </Pagination>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
export default BlogListingPage;

export const query = graphql`
  query BlogListingPage {
    allStrapiBlog(sort: { date: DESC }) {
      edges {
        node {
          id
          title
          slug
          blog_tags {
            id
            slug
            title
          }
          topBanner {
            image {
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 307
                    placeholder: NONE
                    quality: 100
                    height: 184
                  )
                }
              }
            }
          }
          author {
            firstName
            lastName
            subTitle
            about
            profilePicture {
              localFile {
                childImageSharp {
                  id
                  gatsbyImageData(
                    width: 64
                    height: 64
                    quality: 100
                    placeholder: NONE
                  )
                }
              }
            }
          }
        }
      }
    }
    allStrapiBlogTag {
      edges {
        node {
          id
          title
          slug
        }
      }
    }
    strapiBlogHome {
      title
      bigHeader
      ctaText
      ctaLink
      # topbanner: image {
      # localFile {
      #     childImageSharp {
      #       gatsbyImageData(placeholder: NONE, quality: 100, width: 562)
      #     }
      #   }
      # }
      author {
        firstName
        lastName
        subTitle
        about
        strapi_id
        profilePicture {
          localFile {
            childImageSharp {
              gatsbyImageData(
                quality: 100
                placeholder: NONE
                height: 64
                width: 64
              )
            }
          }
        }
      }
    }
    localSearchBlogsIndex {
      id
      store
      index
    }
  }
`;
